import { useEffect, useRef, useState } from 'react'
import {
  EmergencyError,
  unlockGranterVaultFlow,
  type DecryptedVaultItem,
} from '@features/emergency-access/services/emergencyAccessFlow'
import type { VaultItemPayload } from '@features/vault/types'
import { VaultItemAvatar } from '@features/vault/components/VaultItemAvatar'
import { copyToClipboard, getEffectiveClipboardTtlSec } from '@services/clipboard'
import { formatDateJst, formatDateTimeJst } from '@utils/datetime'
import { Notice } from '@shared/ui/Notice'
import { ScrollTopButton } from '@shared/ui/ScrollTopButton'
import { useToast } from '@shared/ui/useToast'
import { CustomFieldsSection, PayloadSections } from '@/pages/VaultDetailPage'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * `#emergency/vault/:id` Read-only emergency view of granter's vault (Track F、Phase 5)。
 *
 * フロー:
 *   1. mount で `unlockGranterVaultFlow(id)` を実行
 *   2. 成功時: granter email + items を表示 (各 item の plaintext JSON を vault 一覧と
 *      同じ行デザインで描画 — type アイコン / 名前 / お気に入り / type タグ / 更新日)
 *   3. 失敗時: messageKey で UI に表示
 *
 * 行アクション (閲覧専用):
 *   - login のみ: username / password の copy (clipboard 90s 自動クリア)
 *   - 全 type: 「URL を開く」(login) と「詳細を表示」(VaultDetailPage と同じ kv 描画の
 *     ポップアップ)。編集は不可なので編集アイコンは出さない。
 * フォルダ名は emergency unlock のレスポンスに含まれないため非表示。
 *
 * 重要: plaintext は string で state に保持される。JS string は zeroize 不可
 * (GC 任せ)。route 離脱で state は GC 候補になるが完全保証なし。
 *
 * #2 fix: unlock は非冪等 (audit ログ + REQUESTED→APPROVED の lazy 承認) なため、
 * React 18 StrictMode の effect 二重実行で 2 回叩くと 429 になる。`cancelled`
 * フラグは 2 回目を握り潰すが 1 回目の結果も捨ててしまう。inviteId をキーにした
 * ref ガードで「同じ id の unlock は 1 回だけ」を保証する。
 */

export interface EmergencyVaultPageProps {
  inviteId: string
}

type VaultState =
  | { kind: 'loading' }
  | {
      kind: 'ready'
      granterEmail: string
      granterName: string | null
      items: DecryptedVaultItem[]
    }
  | { kind: 'error'; messageKey: string }

const ERROR_KEYS: Record<EmergencyError['code'], string> = {
  no_session: 'settings.emergency.error.no_session',
  already_initialized: 'settings.emergency.error.already_initialized',
  self_invite: 'settings.emergency.error.self_invite',
  duplicate_invite: 'settings.emergency.error.duplicate_invite',
  not_found: 'settings.emergency.error.not_found',
  bad_request: 'settings.emergency.error.bad_request',
  forbidden: 'settings.emergency.error.forbidden',
  too_many_requests: 'settings.emergency.error.too_many_requests',
  network: 'settings.emergency.error.network',
  generic: 'settings.emergency.error.generic',
}

const errorKeyFor = (err: unknown): string => {
  if (err instanceof EmergencyError) return ERROR_KEYS[err.code]

  return ERROR_KEYS.generic
}

/** plaintext (cipher の中身 JSON) を VaultItemPayload に parse する。失敗時 null。 */
const parsePayload = (plaintext: string): VaultItemPayload | null => {
  try {
    const p = JSON.parse(plaintext) as VaultItemPayload

    return p && typeof p.type === 'string' ? p : null
  } catch {
    return null
  }
}

/** 行のサブテキスト (vault 一覧の previewFor と同じ方針)。 */
const previewOf = (p: VaultItemPayload): string => {
  if (p.type === 'login') return p.username ?? p.uris?.[0]?.uri ?? p.url ?? ''
  if (p.type === 'card') return p.cardHolder ?? ''
  if (p.type === 'note') return (p.body ?? '').slice(0, 60).replace(/\n+/gu, ' ')

  return [p.firstName, p.lastName].filter(Boolean).join(' ')
}

/** login の代表 URL (「URL を開く」アクション用)。login 以外 / 未設定は null。 */
const urlOf = (p: VaultItemPayload): string | null => {
  if (p.type !== 'login') return null
  const u = p.uris?.[0]?.uri ?? p.url ?? ''

  return u.trim() !== '' ? u : null
}

const formatDate = formatDateJst

const formatTimestamp = formatDateTimeJst

const EmergencyVaultRow = ({
  item,
  onCopy,
  onShowDetail,
}: {
  item: DecryptedVaultItem
  onCopy: (kind: string, value: string | undefined) => void
  onShowDetail: (item: DecryptedVaultItem) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const payload = parsePayload(item.plaintext)

  if (payload === null) {
    return (
      <li className="list-row vault-list-row emergency-vault-row">
        <div className="vault-row-icon" aria-hidden="true">
          <Icon name="file-text" />
        </div>
        <div className="vault-row-main">
          <div className="nm">{t('settings.emergency.vault.parse_failed')}</div>
        </div>
        <div className="vault-row-tag" />
        <div className="vault-row-updated" />
        <div className="acts vault-row-acts" />
      </li>
    )
  }

  const preview = previewOf(payload)
  const url = urlOf(payload)

  return (
    <li
      className="list-row vault-list-row emergency-vault-row"
      data-testid={`emergency-item-${item.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onShowDetail(item)}
      onKeyDown={ev => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault()
          onShowDetail(item)
        }
      }}
      aria-label={payload.name}
    >
      <VaultItemAvatar name={payload.name} className="vault-row-icon" />
      <div className="vault-row-main">
        <div className="nm">
          {payload.name}
          {payload.favorite === true && (
            <span className="vault-row-fav" aria-hidden="true">
              <Icon name="star" />
            </span>
          )}
        </div>
        {preview !== '' && <div className="sb">{preview}</div>}
      </div>
      <div className="vault-row-tag">
        <span className="tag">{t(`vault.type.${payload.type}`)}</span>
      </div>
      <div className="vault-row-updated sb mono">{formatDate(item.updatedAt)}</div>
      <div
        className="acts vault-row-acts"
        role="presentation"
        onClick={ev => ev.stopPropagation()}
        onKeyDown={ev => ev.stopPropagation()}
      >
        {payload.type === 'login' && payload.username !== undefined && payload.username !== '' && (
          <button
            type="button"
            className="icon-btn sm"
            aria-label={t('settings.emergency.vault.copy_username')}
            title={t('settings.emergency.vault.copy_username')}
            onClick={() => onCopy('username', payload.username)}
          >
            <Icon name="user" />
          </button>
        )}
        {payload.type === 'login' && payload.password !== undefined && payload.password !== '' && (
          <button
            type="button"
            className="icon-btn sm"
            aria-label={t('settings.emergency.vault.copy_password')}
            title={t('settings.emergency.vault.copy_password')}
            onClick={() => onCopy('password', payload.password)}
          >
            <Icon name="copy" />
          </button>
        )}
        {url !== null && (
          <a
            className="icon-btn sm"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('vault.detail.action.open_url')}
            title={t('vault.detail.action.open_url')}
          >
            <Icon name="external" />
          </a>
        )}
        <button
          type="button"
          className="icon-btn sm"
          aria-label={t('settings.emergency.vault.show_detail')}
          title={t('settings.emergency.vault.show_detail')}
          onClick={() => onShowDetail(item)}
          data-testid={`emergency-item-detail-${item.id}`}
        >
          <Icon name="info" />
        </button>
      </div>
    </li>
  )
}

/** #5: アイテム詳細ポップアップ (VaultDetailPage の kv 描画を流用、閲覧専用)。 */
const EmergencyDetailDialog = ({
  item,
  onCopy,
  onClose,
}: {
  item: DecryptedVaultItem
  onCopy: (kind: string, value: string | undefined) => void
  onClose: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  const [isPwVisible, setIsPwVisible] = useState(false)
  const payload = parsePayload(item.plaintext)

  // Esc キーでも閉じられるようにする (overlay クリックと同等の離脱手段)。
  useEffect(() => {
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="vault-delete-overlay" role="presentation" onClick={onClose}>
      {/* ダイアログ内クリックは閉じない — overlay (外側) クリックのみ閉じる。 */}
      <div
        className="vault-delete-dialog emergency-detail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="emergency-detail-title"
        onClick={ev => ev.stopPropagation()}
      >
        {payload === null ? (
          <div className="emergency-detail-body">
            <h3 id="emergency-detail-title">{t('settings.emergency.vault.parse_failed')}</h3>
          </div>
        ) : (
          <div className="emergency-detail-body">
            <div className="detail-head">
              <VaultItemAvatar name={payload.name} size="lg" />
              <div className="meta">
                <div className="nm" id="emergency-detail-title">
                  {payload.name}
                  {payload.favorite === true && (
                    <span className="vault-row-fav" aria-hidden="true">
                      <Icon name="star" />
                    </span>
                  )}
                </div>
                <div className="vault-detail-head-tags">
                  <span className="tag">{t(`vault.type.${payload.type}`)}</span>
                </div>
              </div>
            </div>

            <PayloadSections
              payload={payload}
              isPwVisible={isPwVisible}
              onTogglePwVisible={() => setIsPwVisible(v => !v)}
              onCopy={onCopy}
            />

            {payload.fields && payload.fields.length > 0 && (
              <CustomFieldsSection fields={payload.fields} onCopy={onCopy} />
            )}

            {payload.notes !== undefined && payload.notes !== '' && (
              <div className="detail-section">
                <h4>{t('vault.detail.section.notes')}</h4>
                <div className="vault-detail-note-body">{payload.notes}</div>
              </div>
            )}

            <div className="detail-section vault-detail-meta">
              <div className="kv">
                <div className="kv-meta">
                  <div className="k">{t('vault.detail.field.updated_at')}</div>
                  <div className="v mono">{formatTimestamp(item.updatedAt)}</div>
                </div>
              </div>
              <div className="kv">
                <div className="kv-meta">
                  <div className="k">{t('vault.detail.field.created_at')}</div>
                  <div className="v mono">{formatTimestamp(item.createdAt)}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="emergency-detail-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('settings.emergency.vault.detail_close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export const EmergencyVaultPage = ({ inviteId }: EmergencyVaultPageProps): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const [state, setState] = useState<VaultState>({ kind: 'loading' })
  const [detailItem, setDetailItem] = useState<DecryptedVaultItem | null>(null)
  // #2: StrictMode の effect 二重実行でも unlock を 1 回だけに絞る (inviteId 単位)。
  const unlockedIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (unlockedIdRef.current === inviteId) return
    unlockedIdRef.current = inviteId

    void (async (): Promise<void> => {
      try {
        const result = await unlockGranterVaultFlow(inviteId)

        setState({
          kind: 'ready',
          granterEmail: result.granterEmail,
          granterName: result.granterName,
          items: result.items,
        })
      } catch (err: unknown) {
        setState({ kind: 'error', messageKey: errorKeyFor(err) })
      }
    })()
  }, [inviteId])

  const onCopy = async (kind: string, value: string | undefined): Promise<void> => {
    if (value === undefined || value === '') return
    try {
      await copyToClipboard(value)
      showToast({
        titleKey: `vault.detail.toast.copied_${kind}`,
        titleVars: { ttl: getEffectiveClipboardTtlSec() },
        tone: 'success',
        dedupe: true,
      })
    } catch {
      showToast({ titleKey: 'vault.toast.copy_failed', tone: 'danger', dedupe: true })
    }
  }

  return (
    <div className="emergency-vault-page page-body">
      <div className="emergency-vault-head">
        <a href="#settings/emergency" className="btn btn-secondary">
          <Icon name="arrow-left" aria-hidden="true" />
          {t('settings.emergency.vault.back')}
        </a>
        <h1 className="emergency-vault-title">{t('settings.emergency.vault.title')}</h1>
      </div>

      {state.kind === 'loading' && (
        <Notice tone="info" ico="info">
          {t('settings.emergency.vault.loading')}
        </Notice>
      )}

      {state.kind === 'error' && (
        <Notice tone="danger" ico="alert">
          {t(state.messageKey)}
        </Notice>
      )}

      {state.kind === 'ready' && (
        <>
          <Notice tone="warn" ico="alert">
            {t('settings.emergency.vault.intro').replace(
              '{email}',
              state.granterName ?? state.granterEmail,
            )}
          </Notice>

          {state.items.length === 0 ? (
            <div className="settings-item-sub">{t('settings.emergency.vault.empty')}</div>
          ) : (
            <ul className="list vault-list" role="list">
              {state.items.map(it => (
                <EmergencyVaultRow
                  key={it.id}
                  item={it}
                  onCopy={(kind, value) => void onCopy(kind, value)}
                  onShowDetail={setDetailItem}
                />
              ))}
            </ul>
          )}
        </>
      )}

      {detailItem !== null && (
        <EmergencyDetailDialog
          item={detailItem}
          onCopy={(kind, value) => void onCopy(kind, value)}
          onClose={() => setDetailItem(null)}
        />
      )}

      <ScrollTopButton />
    </div>
  )
}
